import {useCallback, useEffect, useRef} from 'react';
import useStableCallback from 'src/public/useStableCallback/useStableCallback';

type AnyFunction = (...args: any[]) => void;

/*
 * Throttles a function by limiting how often it can be invoked.
 *
 * This implementation is:
 * - **Leading**: the first call outside the throttle window runs immediately.
 * - **Trailing**: calls inside the window are coalesced into one call that runs
 *   when the window ends, using the **latest arguments** seen.
 *
 * Notes:
 * - We store the latest args in a ref so repeated calls don't allocate timers.
 * - We store the last *actual invocation time* so the delay is measured from
 *   when the callback really ran (not when it was first scheduled).
 * - `useStableCallback` ensures we always call the latest `callback` without
 *   recreating the throttled function on every render.
 *
 * @param callback - The function to throttle.
 * @param delay - The delay in milliseconds. Defaults to 500ms.
 * @returns A throttled version of the function.
 */
export default function useThrottle<T extends AnyFunction>(
  callback: T,
  delay = 500,
): T {
  const stableCallback = useStableCallback(callback);

  // Non-null means a trailing call is scheduled.
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Timestamp of the last time we invoked the callback.
  const lastCallTimeRef = useRef(-Infinity);

  // Latest arguments passed while throttled; used for the trailing call.
  const latestArgsRef = useRef<Parameters<T> | null>(null);

  // Clears any pending trailing call and drops queued args.
  const cancelScheduled = useCallback(() => {
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    latestArgsRef.current = null;
  }, []);

  const throttled = useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      const elapsed = now - lastCallTimeRef.current;

      // Outside the throttle window: run immediately (leading edge).
      if (elapsed >= delay) {
        cancelScheduled();
        lastCallTimeRef.current = now;
        stableCallback(...args);
        return;
      }

      // Remember the latest arguments.
      latestArgsRef.current = args;

      // If we have a scheduled trailing call, do nothing.
      if (timeoutIdRef.current !== null) {
        return;
      }

      // Schedule a trailing call for exactly when the window ends.
      timeoutIdRef.current = setTimeout(() => {
        timeoutIdRef.current = null;

        const latestArgs = latestArgsRef.current;
        latestArgsRef.current = null;

        if (!latestArgs) {
          return;
        }

        lastCallTimeRef.current = Date.now();
        stableCallback(...latestArgs);
      }, delay - elapsed);
    },
    [cancelScheduled, delay, stableCallback],
  ) as T;

  // Prevent trailing invocation after unmount.
  useEffect(() => cancelScheduled, [cancelScheduled]);

  return throttled;
}
